import moment from "moment-timezone";
import Button from "components/ui/Button";
import { isTaskOverdue, NotificationTaskType, Task } from "services/tasks";
import { getUserFullName } from "services/users/utils";
import { routes } from "pages/routes";
import { useUserTimezone } from "utils/date";

enum TaskTypeName {
  FEEDBACK = "Feedback",
  PLAN = "Lesson plan",
  DEFAULT = "Notification",
}

interface TaskListItemProps {
  item: Task;
  closeDetails: () => void;
}

export function TaskListItem({ item, closeDetails }: TaskListItemProps) {
  const withTimezone = useUserTimezone();

  const getTaskTypeName = (task: Task) => {
    if (task.type === NotificationTaskType.FEEDBACK) {
      return TaskTypeName.FEEDBACK;
    } else if (task.type === NotificationTaskType.PLAN) {
      return TaskTypeName.PLAN;
    }
  };

  const getTaskTitle = (task: Task) => {
    return (
      getTaskTypeName(task) +
      " for " +
      getUserFullName(task.session ? task.session.user : task.user)
    );
  };

  const getTaskDescription = (task: Task) => {
    let description = null;
    if (task.type === NotificationTaskType.FEEDBACK) {
      const sessionTime = task.session?.scheduled
        ? withTimezone.format(task.session.scheduled, "sessionDateTime")
        : "";
      description =
        "Session feedback for lesson with " +
        getUserFullName(task.session?.user) +
        " on " +
        sessionTime +
        " is ";
    } else if (task.type === NotificationTaskType.PLAN) {
      description = "Lesson plan for " + getUserFullName(task.user) + " is ";
    }
    if (isTaskOverdue(task)) {
      description += moment.utc(task.due_date).fromNow(true) + " overdue.";
    } else {
      description += " due " + moment.utc().to(task.due_date) + ".";
    }
    return description;
  };

  const getAction = (task: Task) => {
    let toPath = null;
    let actionName = null;
    if (task.type === NotificationTaskType.FEEDBACK) {
      toPath = task.session?.id
        ? routes.sessionFeedbackEdit.path({ id: task.session.id })
        : null;
      actionName = "Give Feedback";
    } else if (task.type === NotificationTaskType.PLAN) {
      toPath = task.user?.id ? routes.studentDetails.path({ id: task.user.id }) : null;
      actionName = "Create Lesson Plan";
    }
    if (toPath && actionName) {
      return (
        <Button as="link" variant="plain" onClick={closeDetails} to={toPath}>
          {actionName}
        </Button>
      );
    }
    return null;
  };

  return (
    <div className={"notification-list-item" + (isTaskOverdue(item) ? " overdue" : "")}>
      <div className="item-header">
        {getTaskTypeName(item)}
        <span>•</span>
        {moment.utc(item.scheduled).fromNow()}
      </div>
      <div className="item-title">{getTaskTitle(item)}</div>
      <div className="item-description">{getTaskDescription(item)}</div>
      <div className="actions">{getAction(item)}</div>
    </div>
  );
}
